import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Clock, Map, MessageCircle, ChevronDown } from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { LocationMap } from "../components/LocationMap";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { API } from "../lib/api";

export default function ParkingPage() {
  const [parking, setParking] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showMap, setShowMap] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { language } = useLanguage();
  const { location, locationData } = useLocation();

  const translations = {
    en: {
      title: "Parking Guide",
      subtitle: "Best spots near",
      all: "All",
      free: "Free",
      paid: "Paid",
      showMap: "Show map",
      hideMap: "Hide map",
      walkTo: "walk to center",
      viewOnMap: "View on map",
      contact: "Need help? WhatsApp us",
      showMore: "Show more parking",
      showLess: "Show less",
      tip: "Tip"
    },
    it: {
      title: "Guida Parcheggi",
      subtitle: "I migliori posti vicino a",
      all: "Tutti",
      free: "Gratuiti",
      paid: "A pagamento",
      showMap: "Mostra mappa",
      hideMap: "Nascondi mappa",
      walkTo: "a piedi dal centro",
      viewOnMap: "Vedi sulla mappa",
      contact: "Hai bisogno di aiuto? Scrivici su WhatsApp",
      showMore: "Mostra altri parcheggi",
      showLess: "Mostra meno",
      tip: "Consiglio"
    },
    fr: {
      title: "Guide du Parking",
      subtitle: "Meilleurs endroits près de",
      all: "Tous",
      free: "Gratuits",
      paid: "Payants",
      showMap: "Afficher carte",
      hideMap: "Masquer carte",
      walkTo: "à pied du centre",
      viewOnMap: "Voir sur la carte",
      contact: "Besoin d'aide? Écrivez-nous sur WhatsApp",
      showMore: "Afficher plus de parkings",
      showLess: "Afficher moins",
      tip: "Conseil"
    },
    de: {
      title: "Parkplatzführer",
      subtitle: "Beste Plätze in der Nähe von",
      all: "Alle",
      free: "Kostenlos",
      paid: "Kostenpflichtig",
      showMap: "Karte anzeigen",
      hideMap: "Karte ausblenden",
      walkTo: "zu Fuß zum Zentrum",
      viewOnMap: "Auf Karte anzeigen",
      contact: "Brauchen Sie Hilfe? Schreiben Sie uns auf WhatsApp",
      showMore: "Mehr Parkplätze anzeigen",
      showLess: "Weniger anzeigen",
      tip: "Tipp"
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    axios.get(`${API}/parking`).then(res => setParking(res.data)).catch(console.error);
  }, []);

  // Filter by location
  const getLocationParking = () => {
    const locationTowns = {
      monopoli: ['Monopoli', 'Capitolo', 'Polignano a Mare'],
      fasano: ['Fasano', 'Savelletri', 'Ostuni', 'Torre Canne']
    };
    const relevantTowns = locationTowns[location] || locationTowns.monopoli;
    return parking.filter(p => relevantTowns.includes(p.town));
  };

  const filteredParking = getLocationParking().filter(p => {
    if (filter !== "all" && p.spot_type !== filter) return false;
    return true;
  });

  const displayParking = showAll ? filteredParking : filteredParking.slice(0, 5);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="parking-page">
      <header className="bg-gradient-to-b from-stone-900 to-stone-800 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif">{txt.title}</h1>
          <p className="text-stone-400 text-sm mt-2">{txt.subtitle} {locationData?.name}</p>
        </div>
      </header>

      <div className="px-6 py-4 max-w-4xl mx-auto">
        <div className="flex gap-2">
          {[
            { id: "all", label: txt.all },
            { id: "free", label: txt.free },
            { id: "paid", label: txt.paid }
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === f.id ? "bg-teal-600 text-white" : "bg-white border border-stone-200 text-stone-600"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => setShowMap(!showMap)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              showMap ? "bg-teal-600 text-white" : "bg-white border border-stone-200 text-stone-600"
            }`}
          >
            <Map size={14} />
            {showMap ? txt.hideMap : txt.showMap}
          </button>
        </div>
      </div>

      {showMap && displayParking.length > 0 && (
        <div className="px-6 pb-4 max-w-4xl mx-auto">
          <LocationMap locations={displayParking} type="parking" height="300px" zoom={13} />
        </div>
      )}

      <div className="px-6 py-4 max-w-4xl mx-auto space-y-3 pb-24 md:pb-8">
        {displayParking.map((spot) => (
          <article key={spot.id} className="bg-white rounded-xl p-4 border border-stone-100">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-stone-900">{spot.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    spot.spot_type === "free" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"
                  }`}>
                    {spot.cost || (spot.spot_type === "free" ? txt.free : txt.paid)}
                  </span>
                </div>
                <p className="text-sm text-stone-500">{spot.area} · {spot.town}</p>
                <p className="text-sm text-stone-600 mt-2">{spot.description}</p>
                {spot.hours && (
                  <p className="text-sm text-stone-500 mt-1 flex items-center gap-1">
                    <Clock size={12} /> {spot.hours}
                  </p>
                )}
                {spot.tip && (
                  <p className="text-sm text-teal-700 mt-2 italic">{txt.tip}: {spot.tip}</p>
                )}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
              <span className="text-sm text-stone-500 flex items-center gap-1">
                <MapPin size={12} /> {spot.walk_time} {txt.walkTo}
              </span>
              {spot.coordinates && (
                <a 
                  href={`https://www.openstreetmap.org/?mlat=${spot.coordinates.lat}&mlon=${spot.coordinates.lng}#map=17/${spot.coordinates.lat}/${spot.coordinates.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-teal-700 hover:text-teal-800 flex items-center gap-1"
                >
                  <Map size={12} /> {txt.viewOnMap}
                </a>
              )}
            </div>
          </article>
        ))}

        {filteredParking.length > 5 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full py-3 text-teal-700 hover:text-teal-800 font-medium text-sm flex items-center justify-center gap-2"
          >
            {showAll ? txt.showLess : txt.showMore}
            <ChevronDown size={16} className={showAll ? 'rotate-180' : ''} />
          </button>
        )}

        {/* WhatsApp CTA */}
        <a 
          href={`https://wa.me/${locationData?.whatsapp || '393331234567'}`}
          className="block bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors mt-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="text-emerald-600" size={20} />
              <span className="text-emerald-800 font-medium text-sm">{txt.contact}</span>
            </div>
            <span className="text-emerald-600">→</span>
          </div>
        </a>
      </div>
      <Footer />
    </div>
  );
}
